import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { VideoCard, getLikedSet, saveLikedSet } from "../../components/ShotsSection";
import { getShots, Shot } from "../../services/shotsApi";
import { isPrerendering } from "../../utils/prerender";
import PageShell, { PageHero } from "./PageShell";
import { Arrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

const ShotsPageV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";

  const [shots, setShots] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [likedIds, setLikedIds] = useState<Set<string>>(getLikedSet);

  useEffect(() => {
    if (isPrerendering()) return; // snapshot keeps the skeleton
    let alive = true;
    getShots(1, 24)
      .then((d) => alive && setShots(d.shots))
      .catch(() => alive && setShots([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onLike = useCallback((id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveLikedSet(next);
      return next;
    });
  }, []);
  const onActivate = useCallback((id: string) => setActiveId(id), []);
  const onDeactivate = useCallback(
    (id: string) => setActiveId((cur) => (cur === id ? null : cur)),
    [],
  );

  return (
    <PageShell
      seo={{
        title: isFr
          ? "Nos lavages de sneakers en vidéo : avant / après | Lave Tes Baskets"
          : "Our sneaker cleanings on video: before / after | Lave Tes Baskets",
        description: isFr
          ? "Jordan, New Balance, Nike, Louis Vuitton : découvrez nos nettoyages de baskets en vidéo, avant et après passage à l'atelier Lave Tes Baskets en Île-de-France."
          : "Jordan, New Balance, Nike, Louis Vuitton: watch our sneaker cleanings on video, before and after the Lave Tes Baskets workshop in Île-de-France.",
        canonicalPath: route(lang, "shots"),
      }}
    >
      <PageHero
        crumbs={[
          { label: isFr ? "Accueil" : "Home", to: route(lang) },
          { label: t("shots.title") },
        ]}
        index="02"
        eyebrow={t("shots.title")}
        title={
          isFr ? (
            <>
              Le film, <em className="italic text-tangerine">en entier.</em>
            </>
          ) : (
            <>
              The film, <em className="italic text-tangerine">in full.</em>
            </>
          )
        }
        lede={t("shots.pageSubtitle")}
        actions={
          <Link to={`/${lang}/signup`} className="v2-btn v2-btn-solid">
            {isFr ? "Laver ma paire" : "Wash my pair"}
            <Arrow />
          </Link>
        }
      />

      <section className="bg-ink pb-24 text-bone sm:pb-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-3xl bg-bone/5" style={{ aspectRatio: "9/16" }} />
              ))}
            </div>
          ) : shots.length === 0 ? (
            <p className="py-20 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-bone/40">—</p>
          ) : (
            <div className="grid grid-cols-1 justify-items-center gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:gap-6 [&>*]:w-full [&>*]:max-w-[320px]">
              {shots.map((s) => (
                <div key={s.id} className="v2-reveal [&>*]:!w-full">
                  <VideoCard
                    shot={s}
                    globalMuted={true}
                    liked={likedIds.has(s.id)}
                    onLike={onLike}
                    activeId={activeId}
                    onActivate={onActivate}
                    onDeactivate={onDeactivate}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default ShotsPageV2;
